import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Loader from '../components/Loader';
import useLoading from '../hooks/useLoading';
import {
    MapPinIcon,
    PhoneIcon,
    EnvelopeIcon,
    ClockIcon,
    BuildingOfficeIcon,
    HomeIcon,
    BriefcaseIcon,
    QuestionMarkCircleIcon,
    ArrowRightIcon
} from '@heroicons/react/24/outline';

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        projectType: '',
        message: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const isLoading = useLoading(1000);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            // Here you would typically handle form submission to your backend
            await new Promise(resolve => setTimeout(resolve, 1500)); // Simulated API call
            console.log('Form submitted:', formData);

            // Reset form
            setFormData({
                name: '',
                email: '',
                phone: '',
                projectType: '',
                message: '',
            });

            setSubmitStatus('success');
            setTimeout(() => setSubmitStatus(null), 3000);
        } catch (error) {
            console.error('Error submitting form:', error);
            setSubmitStatus('error');
            setTimeout(() => setSubmitStatus(null), 3000);
        } finally {
            setIsSubmitting(false);
        }
    };

    const contactInfo = [
        {
            icon: MapPinIcon,
            title: 'Visit Us',
            details: ['123 Design Street', 'Creative City, ST 12345'],
        },
        {
            icon: PhoneIcon,
            title: 'Call Us',
            details: ['+1 (234) 567-890', 'Mon-Fri: 9am-6pm'],
        },
        {
            icon: EnvelopeIcon,
            title: 'Email Us',
            details: ['info@parthinteriors.com', 'support@parthinteriors.com'],
        },
    ];

    const projectTypes = [
        { value: 'residential', label: 'Residential Design', icon: HomeIcon },
        { value: 'commercial', label: 'Commercial Design', icon: BuildingOfficeIcon },
        { value: 'corporate', label: 'Corporate Design', icon: BriefcaseIcon },
        { value: 'other', label: 'Other', icon: QuestionMarkCircleIcon },
    ];

    if (isLoading) {
        return <Loader />;
    }

    return (
        <div className="min-h-screen bg-dark">
            {/* Hero Section */}
            <section className="relative py-24 bg-primary text-light">
                <div className="absolute inset-0 bg-gradient-to-b from-primary/90 via-primary/70 to-primary/90" />
                <div className="container mx-auto px-4 relative">
                    <motion.div
                        className="text-center"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h1 className="font-serif text-5xl md:text-6xl font-bold mb-6">Contact Us</h1>
                        <p className="text-xl text-light/90 max-w-2xl mx-auto">
                            Let's discuss how we can transform your space into something extraordinary
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Contact Section */}
            <section className="py-24">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                        {/* Contact Form */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="card p-8 bg-dark/50 backdrop-blur-sm"
                        >
                            <h2 className="font-serif text-3xl font-bold text-light mb-6">Get in Touch</h2>
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-light/80 mb-2">
                                        Full Name
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-4 py-3 bg-dark/30 border border-light/10 rounded-lg text-light placeholder-light/50 focus:outline-none focus:border-accent transition-colors duration-300"
                                        placeholder="Enter your full name"
                                        disabled={isSubmitting}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-light/80 mb-2">
                                        Email Address
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-4 py-3 bg-dark/30 border border-light/10 rounded-lg text-light placeholder-light/50 focus:outline-none focus:border-accent transition-colors duration-300"
                                        placeholder="Enter your email address"
                                        disabled={isSubmitting}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="phone" className="block text-sm font-medium text-light/80 mb-2">
                                        Phone Number
                                    </label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-dark/30 border border-light/10 rounded-lg text-light placeholder-light/50 focus:outline-none focus:border-accent transition-colors duration-300"
                                        placeholder="Enter your phone number"
                                        disabled={isSubmitting}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="projectType" className="block text-sm font-medium text-light/80 mb-2">
                                        Project Type
                                    </label>
                                    <select
                                        id="projectType"
                                        name="projectType"
                                        value={formData.projectType}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-4 py-3 bg-dark/30 border border-light/10 rounded-lg text-light focus:outline-none focus:border-accent transition-colors duration-300"
                                        disabled={isSubmitting}
                                    >
                                        <option value="">Select a project type</option>
                                        {projectTypes.map((type) => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label htmlFor="message" className="block text-sm font-medium text-light/80 mb-2">
                                        Your Message
                                    </label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        required
                                        rows="4"
                                        className="w-full px-4 py-3 bg-dark/30 border border-light/10 rounded-lg text-light placeholder-light/50 focus:outline-none focus:border-accent transition-colors duration-300"
                                        placeholder="Tell us about your project"
                                        disabled={isSubmitting}
                                    ></textarea>
                                </div>

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={`w-full btn-primary group inline-flex items-center justify-center ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
                                >
                                    {isSubmitting ? (
                                        <span className="flex items-center">
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-light" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Sending...
                                        </span>
                                    ) : (
                                        <>
                                            Send Message
                                            <ArrowRightIcon className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" />
                                        </>
                                    )}
                                </button>

                                <AnimatePresence>
                                    {submitStatus && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            className={`text-center p-3 rounded-lg ${submitStatus === 'success' ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}
                                        >
                                            {submitStatus === 'success' ? 'Message sent successfully!' : 'Error sending message. Please try again.'}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </form>
                        </motion.div>

                        {/* Contact Information */}
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="space-y-8"
                        >
                            <div>
                                <h2 className="font-serif text-3xl font-bold text-light mb-6">Contact Information</h2>
                                <p className="text-light/80 mb-8">
                                    We're here to help you create the perfect space. Reach out to us through any of the following channels.
                                </p>
                            </div>

                            <div className="space-y-6">
                                {contactInfo.map((info, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.6, delay: index * 0.1 }}
                                        className="card group hover:bg-dark/70 transition-all duration-300"
                                        whileHover={{ y: -5 }}
                                    >
                                        <div className="flex items-start space-x-4">
                                            <motion.div
                                                whileHover={{ scale: 1.1 }}
                                                transition={{ duration: 0.3 }}
                                            >
                                                <info.icon className="w-6 h-6 text-accent mt-1" />
                                            </motion.div>
                                            <div>
                                                <h3 className="font-serif text-xl font-bold text-light mb-2">{info.title}</h3>
                                                {info.details.map((detail, i) => (
                                                    <p key={i} className="text-light/80">{detail}</p>
                                                ))}
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>

                            {/* Business Hours */}
                            <div className="mt-12">
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    className="card p-8 bg-dark/50 backdrop-blur-sm"
                                    whileHover={{ y: -5 }}
                                >
                                    <div className="flex items-center space-x-3 mb-4">
                                        <motion.div
                                            whileHover={{ scale: 1.1 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <ClockIcon className="w-6 h-6 text-accent" />
                                        </motion.div>
                                        <h3 className="font-serif text-xl font-bold text-light">Business Hours</h3>
                                    </div>
                                    <div className="space-y-2 text-light/80">
                                        <p>Monday - Friday: 9:00 AM - 6:00 PM</p>
                                        <p>Saturday: 10:00 AM - 4:00 PM</p>
                                        <p>Sunday: Closed</p>
                                    </div>
                                </motion.div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Contact; 